import { t } from 'i18n';
import {
  ConvertedProduct,
  DiscoveryProduct,
  ProductResponse,
  DiscoveryPromotion,
  ProductInfo,
} from '../types';
import { ProductPriceProps } from '@teko/teko-ecommerce';
import { useRouter } from 'next/router';
import { useDiscoveryProduct, useDiscoverySearch } from './useDiscovery';
import { useMemo, useState, useEffect } from 'react';
import { SameBrandProductConfig, useCart, useFrame } from '../contexts';
import {
  getFrame,
  pvHasConditionToShipAndInstall,
  isDigitalLicenseProduct,
  isNotRefundableProduct,
  passQuantityCheck,
  getNewVariant,
} from 'helpers';
import { useTenantConfig } from '../hooks';
import { LIMIT_ITEM_PER_PAGE_PRODUCT_LIST } from 'constants/index';
export const useDetailPage = (
  serverProduct: ProductResponse,
  convertProductDetail: (el: DiscoveryProduct) => ConvertedProduct,
  getProductUrl: (prod: DiscoveryProduct, skuVariant?: string) => string,
  sameBrandProductConfig?: SameBrandProductConfig,
) => {
  const router = useRouter();
  const { frames } = useFrame();
  const { terminal } = useTenantConfig();
  const { result, isLoading, isError } = useDiscoveryProduct(
    router.query.sku as string,
  );
  const cart = useCart();
  const productData = isError
    ? null
    : !result?.product
    ? serverProduct.product
    : result.product;
  const [selectedPromotion, setSelectedPromotion] =
    useState<DiscoveryPromotion>();
  const [price, setPrice] = useState<ProductPriceProps>();
  const product = useMemo<ConvertedProduct>(() => {
    if (!productData) return null;
    return convertProductDetail(productData);
  }, [productData]);
  const productNotes = useMemo(() => {
    const notes = [];
    if (product) {
      if (isDigitalLicenseProduct(product?.attributes)) {
        notes.push(t('sites.shop-front.hooks.useDetailPage.0'));
      }
      if (isNotRefundableProduct(product?.attributeSet)) {
        notes.push(t('sites.shop-front.hooks.useDetailPage.1'));
      }
    }
    return notes;
  }, [product]);
  const { result: sameBrands, isLoading: loadingSameBrandProducts } =
    useDiscoverySearch({
      _page: 1,
      _limit: LIMIT_ITEM_PER_PAGE_PRODUCT_LIST,
      brands: product?.brand.code,
      excludedSkus: product?.sku,
      terminalCode: terminal,
      ...(sameBrandProductConfig || {}),
    });
  const sameBrandProducts = useMemo(() => {
    if (!result) return [];
    return sameBrands.products;
  }, [result]);
  const stockAvailable = useMemo(() => {
    if (product?.needManageStock) {
      return product?.totalAvailable || 0;
    }
    return 999;
  }, [product]);
  const hasShippingAndWarningNote = useMemo(() => {
    if (!product?.categories?.length) {
      return false;
    }
    // TODO: this converts to unknown first looks bad, need refractoring later
    return pvHasConditionToShipAndInstall(product as unknown as ProductInfo);
  }, [product]);
  const onSelectVariant = (variant: any, index: number): void => {
    const newSku = getNewVariant(productData, variant, index);
    if (newSku) {
      router.replace(
        {
          pathname: `/products/[sku]`,
          query: {
            sku: newSku,
          },
        },
        {
          pathname: getProductUrl(productData, newSku),
          query: {
            sku: newSku,
          },
        },
      );
    }
  };
  const showStock =
    product?.status?.sellable &&
    !product?.flashSale &&
    product?.needManageStock === true &&
    stockAvailable < 5 &&
    stockAvailable > 0 &&
    !product.isBundle;
  const isCanAddToCard =
    product &&
    (product.isBundle ||
      passQuantityCheck(
        product?.needManageStock,
        stockAvailable,
        (cart?.order?.orderLines || []).find(
          (orderline: any) => orderline.sku === product.sku,
        )?.quantity,
      ));
  const isOutOfStock =
    product?.totalAvailable !== null && product?.totalAvailable <= 0;
  const showFlashSale = useMemo(() => {
    if (!product) return false;
    if (!product.flashSale) return false;
    const { total, used } = product.flashSale;
    const remain = Math.min(total - used, stockAvailable);
    return remain > 0;
  }, [product, stockAvailable]);
  const handleSetPrice = () => {
    if (product) {
      let selectedPrice = isNaN(+product?.price.priceWithoutSelectionPromotion)
        ? product.price.price
        : +product.price.priceWithoutSelectionPromotion;
      const supplierRetailPrice = isNaN(+product.price.supplierRetailPrice)
        ? undefined
        : +product.price.supplierRetailPrice;
      if (selectedPromotion?.id) {
        selectedPrice = selectedPromotion?.price?.sellPrice;
      }
      setPrice({
        latestPrice: isNaN(+selectedPrice) ? undefined : Number(selectedPrice),
        supplierRetailPrice,
      });
    }
  };
  useEffect(() => {
    if (product) {
      handleSetPrice();
      if (product.selectionPromotions.length)
        setSelectedPromotion(product.selectionPromotions[0]);
    }
  }, [JSON.stringify(product)]);
  useEffect(() => {
    handleSetPrice();
  }, [selectedPromotion?.id]);
  return {
    loading: isLoading,
    product,
    primitiveProduct: productData,
    productNotes,
    frame: getFrame(product?.tags || [], frames),
    onSelectVariant,
    stockAvailable,
    showStock,
    isOutOfStock,
    loadingSameBrandProducts,
    sameBrandProducts,
    hasShippingAndWarningNote,
    isCanAddToCard,
    showFlashSale,
    selectedPromotion,
    setSelectedPromotion,
    price,
    setPrice,
  };
};
